// What the subject tagger sees for one saved item: one picture (or none) and
// a few lines of text.
//
// The picture is chosen the way the mobile Library chooses a tile's
// thumbnail (src/lib/library/libraryCard.ts), so an item is tagged by what
// the user actually sees. Text comes from the title, description, caption,
// tweet text or note body, stripped of HTML and cut short.
//
// Pure TypeScript with no imports: used by the tag-subjects edge function
// (Deno) and by the app's trigger and tests alike.

export interface SubjectItemRow {
  id: string;
  item_type: string;
  content: unknown;
}

export interface SubjectInput {
  /** An http(s) image the model can fetch, or null for text-only items. */
  imageUrl: string | null;
  /** Plain text, at most MAX_TEXT characters. May be empty. */
  text: string;
  /** Short hint for the model: "image", "link (instagram.com)", "note"... */
  kind: string;
}

export const MAX_TEXT = 600;

/** Never tagged: folders and synced copies (the original carries the subjects). */
const SKIPPED_TYPES = new Set(['board_link', 'synced_item']);

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'", '#x27': "'", hellip: '...', mdash: '-', ndash: '-',
};

function decodeEntities(s: string): string {
  return s
    .replace(/&#(\d+);/g, (m, n) => {
      const code = Number(n);
      return code > 0 && code < 0x110000 ? String.fromCodePoint(code) : m;
    })
    .replace(/&([a-z0-9#]+);/gi, (m, name: string) => ENTITIES[name.toLowerCase()] ?? m);
}

/** HTML or plain text to one tidy paragraph. */
export function plainText(input: string): string {
  return decodeEntities(
    input
      .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, ' '),
  )
    .replace(/[ \t\f\v\u00a0]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function truncate(s: string, max = MAX_TEXT): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}...`;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function youTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1) || null;
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname.startsWith('/shorts/')) return u.pathname.split('/')[2] || null;
      return u.searchParams.get('v');
    }
  } catch {
    // not a URL
  }
  return null;
}

/** Instagram's placeholder sprites are not the post (same rule as socialCard.isUsableSocialImage). */
function isPlaceholderSocialImage(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname === 'static.cdninstagram.com' || u.pathname.includes('/rsrc.php');
  } catch {
    return true;
  }
}

/** Only absolute http(s) raster images: vision models cannot read SVG, TIFF or HEIC, or a relative path. */
export function usableImageUrl(url: unknown): string | null {
  const s = str(url).trim();
  if (!/^https?:\/\//i.test(s)) return null;
  if (/\.(svg|tiff?|heic|heif|pdf)(\?|#|$)/i.test(s)) return null;
  return s;
}

function joinText(parts: string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const t = plainText(p);
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return truncate(out.join('\n'));
}

/** A link still waiting on its deep preview fetch: tag it once the preview lands. */
export function isAwaitingPreview(row: Pick<SubjectItemRow, 'item_type' | 'content'>): boolean {
  return row.item_type === 'link' && obj(row.content).previewPending === true;
}

/** Whether this item is ever tagged. */
export function isTaggable(row: Pick<SubjectItemRow, 'item_type'>): boolean {
  return !SKIPPED_TYPES.has(row.item_type);
}

/**
 * The image and text to tag one item from, or null when the item is never
 * tagged (folders, synced copies) or has nothing to tag from.
 */
export function subjectInputFor(row: SubjectItemRow): SubjectInput | null {
  if (!isTaggable(row)) return null;
  const c = obj(row.content);
  // Shared by every type: what earlier AI passes wrote about the item
  const aiText = [str(c.summary), str(c.aiDescription), str(c.alt)].filter((s) => s && s !== 'Image');

  let imageUrl: string | null = null;
  let text = '';
  let kind: string = row.item_type;

  switch (row.item_type) {
    case 'sticky':
      kind = 'note';
      text = joinText([str(c.text)]);
      break;
    case 'text_card':
    case 'document':
      kind = row.item_type === 'document' ? 'document' : 'note';
      text = joinText([str(c.title), str(c.body) || str(c.content)]);
      break;
    case 'image':
      imageUrl = usableImageUrl(c.url);
      text = joinText([...aiText, str(c.filename)]);
      break;
    case 'video':
      imageUrl = usableImageUrl(c.thumbnailUrl);
      text = joinText([...aiText, str(c.filename), str(c.transcript).slice(0, 400)]);
      break;
    case 'file':
      kind = `file (${str(c.filename) || 'unnamed'})`;
      imageUrl = usableImageUrl(c.thumbnailUrl);
      text = joinText([str(c.filename), ...aiText, str(c.extracted_text).slice(0, 800)]);
      break;
    case 'scraped_creative': {
      const handle = str(c.account_handle);
      kind = `social post${str(c.platform) ? ` (${str(c.platform)})` : ''}`;
      imageUrl = usableImageUrl(c.thumbnail_url);
      text = joinText([str(c.hook_text), str(c.caption), handle ? `@${handle}` : '', ...aiText]);
      break;
    }
    case 'link': {
      if (c.tweet_id) {
        kind = 'X post';
        const media = Array.isArray(c.media) ? (c.media as unknown[]).map(obj) : [];
        const first = media[0];
        imageUrl = first ? usableImageUrl(str(first.preview_url) || (str(first.type) === 'photo' ? str(first.url) : '')) : null;
        const author = obj(c.author);
        text = joinText([str(c.text), str(author.name)]);
        break;
      }
      const url = str(c.url);
      const host = hostOf(url);
      kind = host ? `link (${host})` : 'link';
      const image = str(c.image);
      const social = /instagram\.com|tiktok\.com/.test(host);
      const ytId = youTubeId(url);
      const picked = social
        ? (image && !isPlaceholderSocialImage(image) ? image : '')
        : image || str(c.screenshot) || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '');
      imageUrl = usableImageUrl(picked);
      text = joinText([str(c.title), str(c.description), str(c.siteName), ...aiText]);
      break;
    }
    default:
      imageUrl = usableImageUrl(c.url) ?? usableImageUrl(c.thumbnailUrl);
      text = joinText([str(c.title), str(c.text), str(c.body), ...aiText]);
  }

  if (!imageUrl && !text) return null;
  return { imageUrl, text, kind };
}
